import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  CalendarDays,
  Check,
  ClipboardList,
  RefreshCw,
  Settings,
  Upload,
  UserCheck,
} from "lucide-react";
import { fetchWfAnalytics, fetchWfEnabledModes, fetchWfHolidays } from "../../../Moduels/attendance/wfApi";
import { fetchAttendance, normalizeAttendanceStatus } from "../../../Moduels/attendance/attendanceApi";
import { getEmployees } from "../../../Moduels/Employees/EmployeeApi";
import { ATTENDANCE_LEGACY_PATHS } from "../../../Moduels/attendance/attendanceNavConfig";
import DatePickerField from "../../common/DatePickerField";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import { hasRole, R } from "../../../rbac/access";
import "../../../styles/attendance-mvp.css";

const MODE_LABELS = {
  default_present: "Default Present",
  manual_hr: "Manual Entry",
  excel_upload: "Excel Upload",
  biometric: "Biometric",
};

const QUICK_ACTIONS = [
  {
    label: "Mark Attendance",
    to: "/attendance/mark",
    icon: UserCheck,
    variant: "mark",
  },
  {
    label: "Upload Attendance",
    to: ATTENDANCE_LEGACY_PATHS.import,
    icon: Upload,
    variant: "upload",
  },
  {
    label: "View Calendar",
    to: "/attendance/apply-calendar",
    icon: CalendarDays,
    variant: "calendar",
  },
  {
    label: "Upload Holiday Calendar",
    to: "/holiday-calendar",
    icon: ClipboardList,
    variant: "holiday",
  },
];

function formatDashboardDate(iso) {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const day = String(d.getDate()).padStart(2, "0");
  return `${weekdays[d.getDay()]}, ${day}-${months[d.getMonth()]}-${d.getFullYear()}`;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function countFromAnalytics(data, keys) {
  const raw = data?.status_counts || {};
  return keys.reduce((sum, k) => sum + (Number(raw[k]) || 0), 0);
}

function countRecordsByStatus(records, codes) {
  const wanted = new Set(codes.map((c) => normalizeAttendanceStatus(c)));
  return records.filter((r) => wanted.has(normalizeAttendanceStatus(r.status))).length;
}

export default function WfDashboard() {
  const { role } = useContext(AuthContext);
  const canConfigureSetup = hasRole(role, [R.ADMIN]);
  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [analytics, setAnalytics] = useState(null);
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [enabledModes, setEnabledModes] = useState([]);
  const [holidayCount, setHolidayCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    const year = selectedDate.slice(0, 4);
    try {
      const [an, att, empRes, modesRes, holidays] = await Promise.all([
        fetchWfAnalytics(selectedDate, selectedDate),
        fetchAttendance({ from_date: selectedDate, to_date: selectedDate }),
        getEmployees({ page: 1, page_size: 500, active_only: true }),
        fetchWfEnabledModes().catch(() => []),
        fetchWfHolidays({ from_date: `${year}-01-01`, to_date: `${year}-12-31` }).catch(() => []),
      ]);
      setAnalytics(an);
      setRecords(Array.isArray(att) ? att : []);
      const items = Array.isArray(empRes?.items) ? empRes.items : Array.isArray(empRes) ? empRes : [];
      setEmployees(items);
      const modes = Array.isArray(modesRes) ? modesRes : modesRes?.enabled_modes || modesRes?.modes || [];
      setEnabledModes(modes);
      setHolidayCount(Array.isArray(holidays) ? holidays.length : 0);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : detail?.message || e?.message || "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    load();
  }, [load]);

  const totalEmployees = employees.length;
  const knownModes = ["default_present", "manual_hr", "excel_upload", "biometric"];
  const modeSet = new Set((enabledModes || []).map((m) => (typeof m === "string" ? m : m.code)));
  const defaultPresentMode = modeSet.has("default_present");

  const presentCount = defaultPresentMode
    ? countRecordsByStatus(records, ["P", "present", "Present"])
    : countFromAnalytics(analytics, ["P", "present", "Present"]);
  const absentCount = defaultPresentMode
    ? countRecordsByStatus(records, ["A", "absent", "Absent"])
    : countFromAnalytics(analytics, ["A", "absent", "Absent"]);
  const leaveCount = defaultPresentMode
    ? countRecordsByStatus(records, ["L", "PL", "UL", "leave", "Leave", "paid_leave", "unpaid_leave"])
    : countFromAnalytics(analytics, ["L", "PL", "UL", "leave", "Leave"]);

  const pctBase = totalEmployees || presentCount + absentCount + leaveCount || 1;
  const presentPct = ((presentCount / pctBase) * 100).toFixed(2);
  const absentPct = ((absentCount / pctBase) * 100).toFixed(2);
  const leavePct = ((leaveCount / pctBase) * 100).toFixed(2);

  // Used by the Alerts card (hidden for now).
  // eslint-disable-next-line no-unused-vars
  const alerts = useMemo(() => {
    const list = [];
    const missing = defaultPresentMode
      ? 0
      : Math.max(0, totalEmployees - records.length);
    if (missing > 0) {
      list.push(`${missing} employee${missing === 1 ? "" : "s"} have missing attendance.`);
    }
    if (!defaultPresentMode && records.length === 0 && totalEmployees > 0) {
      const d = new Date(`${selectedDate}T00:00:00`);
      const label = d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
      list.push(`Attendance not uploaded for ${label}.`);
    }
    if (holidayCount === 0) {
      list.push("Holiday calendar not uploaded for current year.");
    }
    if (analytics?.open_exceptions > 0) {
      list.push(`${analytics.open_exceptions} open exception${analytics.open_exceptions === 1 ? "" : "s"} need review.`);
    }
    return list;
  }, [totalEmployees, records.length, selectedDate, holidayCount, analytics?.open_exceptions, defaultPresentMode]);

  return (
    <div className="att-mvp att-mvp-dashboard-page bw-page">
      <header className="bw-page-header att-mvp-dashboard-header">
        <div className="att-mvp-dashboard-head-inner">
          <div className="att-mvp-dashboard-head-text">
            <h1 className="bw-page-title">Attendance Dashboard</h1>
            <p className="bw-page-subtitle">
              Overview of today&apos;s attendance and important alerts.
            </p>
          </div>
          <div className="att-mvp-dashboard-head-actions">
            <div className="att-mvp-date-wrap att-mvp-dashboard-date-picker">
              <DatePickerField
                label=""
                compact
                hideLabel
                value={selectedDate}
                onChange={setSelectedDate}
                showClear={false}
                formatDisplayFn={formatDashboardDate}
                inputClassName="att-mvp-dashboard-date-trigger att-mvp-dashboard-date-trigger--hero"
              />
            </div>
            <button
              type="button"
              className="att-mvp-btn att-mvp-btn--header att-mvp-btn--header-hero"
              onClick={load}
              disabled={loading}
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      {err && (
        <div className="att-mvp-card" style={{ marginBottom: 14, borderColor: "#fecaca", background: "#fef2f2" }}>
          <div className="att-mvp-card-body" style={{ display: "flex", gap: 10, color: "#991b1b", fontSize: 13 }}>
            <AlertCircle size={18} />
            <span>{typeof err === "string" ? err : "Failed to load"}</span>
          </div>
        </div>
      )}

      <div className="att-mvp-kpi-grid">
        <div className="att-mvp-kpi att-mvp-kpi--employees">
          <p className="att-mvp-kpi-label">Total Employees</p>
          <p className="att-mvp-kpi-value">{totalEmployees}</p>
          <p className="att-mvp-kpi-hint">All active employees</p>
        </div>
        <div className="att-mvp-kpi att-mvp-kpi--present">
          <p className="att-mvp-kpi-label">Present</p>
          <p className="att-mvp-kpi-value">{presentCount}</p>
          <p className="att-mvp-kpi-hint">
            {presentPct}% of total
            {defaultPresentMode ? " · includes default present" : ""}
          </p>
        </div>
        <div className="att-mvp-kpi att-mvp-kpi--absent">
          <p className="att-mvp-kpi-label">Absent</p>
          <p className="att-mvp-kpi-value">{absentCount}</p>
          <p className="att-mvp-kpi-hint">{absentPct}% of total</p>
        </div>
        <div className="att-mvp-kpi att-mvp-kpi--leave">
          <p className="att-mvp-kpi-label">Leave</p>
          <p className="att-mvp-kpi-value">{leaveCount}</p>
          <p className="att-mvp-kpi-hint">{leavePct}% of total</p>
        </div>
      </div>

      <div className="att-mvp-split">
        {/* Alerts card — hidden for now.
        <div className="att-mvp-card att-mvp-dashboard-alerts">
          <div className="att-mvp-card-head att-mvp-dashboard-alerts-head">
            <Bell size={16} />
            Alerts
          </div>
          <div className="att-mvp-card-body">
            {alerts.length ? (
              <ul className="att-mvp-alert-list">
                {alerts.map((a) => (
                  <li key={a} className="att-mvp-alert-item">
                    <AlertCircle size={16} className="att-mvp-alert-icon" />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: "var(--slate-500)" }}>No alerts for this date.</p>
            )}
            <Link to="/attendance/exceptions" className="att-mvp-link" style={{ display: "inline-block", marginTop: 12 }}>
              View All Alerts
            </Link>
          </div>
        </div>
        */}

        <div className="att-mvp-card att-mvp-dashboard-method" style={{ gridColumn: "1 / -1" }}>
          <div className="att-mvp-card-head att-mvp-dashboard-method-head">
            <Settings size={16} />
            <span className="att-mvp-dashboard-method-title">Attendance Method</span>
            {canConfigureSetup ? (
              <Link to="/attendance/setup" className="att-mvp-link att-mvp-dashboard-method-config">
                Configure in Setup
              </Link>
            ) : null}
          </div>
          <div className="att-mvp-card-body att-mvp-dashboard-method-body">
            {/* Only the method(s) currently configured are listed. */}
            <div className="att-mvp-dashboard-method-row">
              <span className="att-mvp-dashboard-method-source">Attendance Source:</span>
              {knownModes.filter((code) => modeSet.has(code)).length ? (
                knownModes
                  .filter((code) => modeSet.has(code))
                  .map((code) => (
                    <span key={code} className="att-mvp-method-item is-on att-mvp-dashboard-method-pill">
                      <Check size={14} />
                      {MODE_LABELS[code] || code}
                    </span>
                  ))
              ) : (
                <span className="att-mvp-dashboard-method-empty">Not configured yet.</span>
              )}
            </div>
            {!canConfigureSetup ? (
              <p className="att-mvp-dashboard-method-note">
                Attendance method changes require admin access in Attendance Setup.
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <p className="att-mvp-dashboard-quick-title">Quick Actions</p>
      <div className="att-mvp-actions att-mvp-dashboard-actions">
        {QUICK_ACTIONS.map(({ label, to, icon: Icon, variant }) => (
          <Link key={to} to={to} className={`att-mvp-action att-mvp-action--${variant}`}>
            <Icon size={22} strokeWidth={2} />
            <span className="att-mvp-action-label">{label}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}
